import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, RefreshCw, AlertCircle, Check, Settings } from 'lucide-react';
import { settingsApi, type SettingDto, type AllSettingsDto } from '../services/settingsApi';
import './SettingsPage.css';

interface EditedSetting {
  category: string;
  key: string;
  value: string;
  originalValue: string;
}

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const [editedSettings, setEditedSettings] = useState<Record<string, EditedSetting>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: settings, isLoading, error, refetch } = useQuery<AllSettingsDto>({
    queryKey: ['settings'],
    queryFn: settingsApi.getAllSettings,
  });

  const updateMutation = useMutation({
    mutationFn: ({ category, key, value }: { category: string; key: string; value: string }) =>
      settingsApi.updateSetting(category, key, value),
    onSuccess: (_, variables) => {
      const settingKey = `${variables.category}:${variables.key}`;
      // Remove from edited settings
      setEditedSettings(prev => {
        const updated = { ...prev };
        delete updated[settingKey];
        return updated;
      });
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      // Show success indicator
      setSavedKey(settingKey);
      setTimeout(() => setSavedKey(null), 2000);
    },
    onError: (error: Error) => {
      setErrorMessage(error.message || 'Failed to save setting');
      setTimeout(() => setErrorMessage(null), 5000);
    },
    onSettled: () => {
      setSavingKey(null);
    },
  });

  const getSettingKey = (category: string, key: string) => `${category}:${key}`;

  const handleValueChange = (setting: SettingDto, newValue: string) => {
    const settingKey = getSettingKey(setting.category, setting.key);

    if (newValue === setting.value) {
      // Remove from edited if value matches original
      setEditedSettings(prev => {
        const updated = { ...prev };
        delete updated[settingKey];
        return updated;
      });
    } else {
      setEditedSettings(prev => ({
        ...prev,
        [settingKey]: {
          category: setting.category,
          key: setting.key,
          value: newValue,
          originalValue: setting.value,
        },
      }));
    }
  };

  const getCurrentValue = (setting: SettingDto): string => {
    const settingKey = getSettingKey(setting.category, setting.key);
    return editedSettings[settingKey]?.value ?? setting.value;
  };

  const hasChanges = (setting: SettingDto): boolean => {
    const settingKey = getSettingKey(setting.category, setting.key);
    return settingKey in editedSettings;
  };

  const handleSave = async (setting: SettingDto) => {
    const settingKey = getSettingKey(setting.category, setting.key);
    const edited = editedSettings[settingKey];

    if (!edited) return;

    setSavingKey(settingKey);
    setErrorMessage(null);

    updateMutation.mutate({
      category: setting.category,
      key: setting.key,
      value: edited.value,
    });
  };

  const handleRevert = (setting: SettingDto) => {
    const settingKey = getSettingKey(setting.category, setting.key);
    setEditedSettings(prev => {
      const updated = { ...prev };
      delete updated[settingKey];
      return updated;
    });
  };

  const renderInput = (setting: SettingDto) => {
    const currentValue = getCurrentValue(setting);
    const settingKey = getSettingKey(setting.category, setting.key);
    const isModified = hasChanges(setting);
    const isSaving = savingKey === settingKey;
    const isSaved = savedKey === settingKey;
    const inputId = `setting-input-${settingKey.replace(/[^a-zA-Z0-9-]/g, '-')}`;

    const inputProps = {
      id: inputId,
      value: currentValue,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
        handleValueChange(setting, e.target.value),
      disabled: isSaving,
      className: `setting-input ${isModified ? 'modified' : ''} ${isSaved ? 'saved' : ''}`,
    };

    switch (setting.dataType.toLowerCase()) {
      case 'boolean':
        return (
          <select {...inputProps}>
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        );
      case 'integer':
      case 'number':
        return (
          <input
            type="number"
            {...inputProps}
          />
        );
      case 'text':
      case 'string':
      default:
        // Use textarea for longer values
        if (currentValue.length > 50 || currentValue.includes('\n')) {
          return (
            <textarea
              {...inputProps}
              rows={3}
            />
          );
        }
        return (
          <input
            type="text"
            {...inputProps}
          />
        );
    }
  };

  if (error) {
    return (
      <div className="settings-page">
        <div className="error-banner">
          <AlertCircle size={20} />
          <span>Error loading settings: {(error as Error).message}</span>
          <button onClick={() => refetch()} className="btn btn-sm">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div className="settings-header-left">
          <div className="page-title-row">
            <Settings size={28} />
            <h1>Settings</h1>
          </div>
          <p className="settings-description">
            Configure application settings. Changes are saved individually per setting.
          </p>
        </div>
        <button
          className="btn btn-outline refresh-btn"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw size={16} className={isLoading ? 'spinning' : ''} />
          Refresh
        </button>
      </div>

      {errorMessage && (
        <div className="error-banner">
          <AlertCircle size={20} />
          <span>{errorMessage}</span>
          <button onClick={() => setErrorMessage(null)} className="btn-close">×</button>
        </div>
      )}

      {isLoading ? (
        <div className="loading-state">
          <RefreshCw size={24} className="spinning" />
          <span>Loading settings...</span>
        </div>
      ) : (
        <div className="settings-categories">
          {settings?.categories.map((category) => (
            <div key={category.category} className="settings-category">
              <h2 className="category-title">{formatCategoryName(category.category)}</h2>
              <div className="settings-list">
                {category.settings.map((setting) => {
                  const settingKey = getSettingKey(setting.category, setting.key);
                  const inputId = `setting-input-${settingKey.replace(/[^a-zA-Z0-9-]/g, '-')}`;
                  const isModified = hasChanges(setting);
                  const isSaving = savingKey === settingKey;
                  const isSaved = savedKey === settingKey;

                  return (
                    <div key={setting.key} className={`setting-row ${isModified ? 'modified' : ''}`}>
                      <div className="setting-info">
                        <label className="setting-key" htmlFor={inputId}>
                          {formatSettingKey(setting.key)}
                        </label>
                        {setting.description && (
                          <p className="setting-description">{setting.description}</p>
                        )}
                        <span className="setting-meta">
                          Type: {setting.dataType}
                          {setting.modifiedAt && ` • Last modified: ${formatDate(setting.modifiedAt)}`}
                        </span>
                      </div>
                      <div className="setting-value">
                        {renderInput(setting)}
                      </div>
                      <div className="setting-actions">
                        {isSaved && (
                          <span className="saved-indicator">
                            <Check size={16} />
                            Saved
                          </span>
                        )}
                        {isModified && !isSaved && (
                          <>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleSave(setting)}
                              disabled={isSaving}
                            >
                              {isSaving ? (
                                <>
                                  <RefreshCw size={14} className="spinning" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Save size={14} />
                                  Save
                                </>
                              )}
                            </button>
                            <button
                              className="btn btn-sm btn-outline"
                              onClick={() => handleRevert(setting)}
                              disabled={isSaving}
                            >
                              Revert
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {(!settings?.categories || settings.categories.length === 0) && (
            <div className="empty-state">
              <p>No settings configured.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Helper functions
function formatCategoryName(category: string): string {
  return category
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

function formatSettingKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
